import { Link } from 'react-router-dom'
import { useId, useState } from 'react'
import { SortableTable } from '@/components/SortableTable'
import { SecurityTrafficLight } from '@/components/SecurityTrafficLight'
import { SnapshotCard } from '@/components/SnapshotCard'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DEFENDER_FIELDS,
  EVENT_SECTION_SLUGS,
  SECURITY_SECTIONS,
  SNAPSHOT_SECTION_SLUGS,
  snapshotTitle,
} from '@/lib/securityScripts'
import {
  evaluateSecurityPosture,
  thirdPartyAvCoversDefender,
  TRAFFIC,
} from '@/lib/securityTrafficLight'
import { detectDotNet6FromPayload, dotNet6MatchSummary } from '@/lib/dotnetDetection'
import { toolkitSummaryFromRmmData } from '@/lib/toolkitHealth'
import { normaliseIssue } from '@/lib/trafficLight'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB')
}

function formatPayloadKey(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatEnumLabel(value) {
  if (value == null || value === '') return '—'
  return String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function SnapshotField({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value)
  const isThreat =
    label.toLowerCase().includes('threat') &&
    text.toUpperCase().includes('THREAT')

  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {isThreat ? (
        <Badge variant="destructive">{text}</Badge>
      ) : (
        <span className="text-right font-medium">{text}</span>
      )}
    </div>
  )
}

function isDefenderOff(value) {
  if (value === false) return true
  if (value == null || value === '') return false
  const text = String(value).trim().toLowerCase()
  return text === 'off' || text === 'disabled' || text === 'false' || text === 'no'
}

function threatdownCoversDefender(threatdown) {
  if (!threatdown) return false
  const protection = String(threatdown.protection_status ?? '').toLowerCase()
  return protection === 'active' || protection === 'protected'
}

function defenderCardStatus(payload = {}, threatdown = null, avProduct = null, avStatus = null) {
  const coveredByThreatdown = threatdownCoversDefender(threatdown)
  const coveredByThirdParty = thirdPartyAvCoversDefender(avProduct, avStatus)
  const covered = coveredByThreatdown || coveredByThirdParty
  const coverLabel = coveredByThreatdown
    ? 'ThreatDown'
    : coveredByThirdParty
      ? String(avProduct || '').trim() || 'third-party AV'
      : null
  const issues = []
  const threats = String(payload.defender_threats ?? '').toUpperCase()
  if (threats.includes('THREAT') && !threats.includes('CLEAN')) issues.push('Threats reported')

  if (!covered) {
    if (isDefenderOff(payload.defender_realtime)) issues.push('Real-time protection off')
    if (isDefenderOff(payload.defender_antivirus)) issues.push('Antivirus disabled')
    const defenderStatus = String(payload.defender_status ?? '').toLowerCase()
    if (
      defenderStatus &&
      !['active', 'enabled', 'ok', 'healthy', 'running'].some((x) =>
        defenderStatus.includes(x),
      )
    ) {
      issues.push('Status needs review')
    }
  }

  if (issues.length === 0) {
    if (
      covered &&
      coverLabel &&
      (isDefenderOff(payload.defender_realtime) ||
        isDefenderOff(payload.defender_antivirus) ||
        String(payload.defender_status ?? '')
          .toLowerCase()
          .includes('inactive'))
    ) {
      return {
        pill: `OK (${coverLabel})`,
        pillVariant: 'ready',
        collapsedSummary: `Inactive — expected while ${coverLabel} protects this device`,
        tone: 'ok',
        coverNote: coverLabel,
      }
    }
    return { pill: 'OK', pillVariant: 'ready', collapsedSummary: null, tone: 'ok', coverNote: null }
  }
  const severe =
    (threats.includes('THREAT') && !threats.includes('CLEAN')) ||
    (!covered &&
      (isDefenderOff(payload.defender_realtime) ||
        isDefenderOff(payload.defender_antivirus)))
  return {
    pill: severe ? 'Action needed' : 'Review',
    pillVariant: severe ? 'destructive' : 'pending',
    collapsedSummary: issues.join(' · '),
    tone: severe ? 'bad' : 'warn',
    coverNote: null,
  }
}

function DefenderSnapshotCard({ snapshot, threatdown, avProduct, avStatus }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const status = defenderCardStatus(data, threatdown, avProduct, avStatus)
  return (
    <SnapshotCard
      title="Windows Defender"
      collectedAt={snapshot.collected_at}
      idPrefix="security-card"
      pill={status.pill}
      pillVariant={status.pillVariant}
      collapsedSummary={status.collapsedSummary || undefined}
      className={
        status.tone === 'bad'
          ? 'border-destructive/40'
          : status.tone === 'warn'
            ? 'border-amber-500/30'
            : undefined
      }
    >
      {Object.entries(DEFENDER_FIELDS).map(([key, label]) => (
        <SnapshotField key={key} label={label} value={data[key]} />
      ))}
      {status.coverNote ? (
        <p className="pt-2 text-xs text-muted-foreground">
          Defender inactive or disabled is expected when {status.coverNote} is protecting this
          device. It is not treated as Action needed.
        </p>
      ) : null}
    </SnapshotCard>
  )
}

function BitLockerSnapshotCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const volumes = Array.isArray(data.volumes) ? data.volumes : []
  const systemEncrypted = data.system_drive_encrypted === true

  return (
    <SnapshotCard
      title="BitLocker"
      collectedAt={snapshot.collected_at}
      pill={systemEncrypted ? 'Encrypted' : 'Not encrypted'}
      pillVariant={systemEncrypted ? 'ready' : 'pending'}
    >
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">System drive encrypted</span>
        <Badge variant={systemEncrypted ? 'success' : 'warning'}>
          {systemEncrypted ? 'Yes' : 'No'}
        </Badge>
      </div>
      <SnapshotField
        label="System drive protection"
        value={formatEnumLabel(data.system_drive_protection)}
      />
      <SnapshotField
        label="Volume count"
        value={data.volume_count ?? volumes.length}
      />

      {volumes.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Volumes</p>
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Drive</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Protection</th>
                  <th className="px-3 py-2 font-medium">Method</th>
                  <th className="px-3 py-2 font-medium text-right">Encrypted</th>
                </tr>
              </thead>
              <tbody>
                {volumes.map((vol, index) => (
                  <tr key={`${vol.mount_point}-${index}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{vol.mount_point || '—'}</td>
                    <td className="px-3 py-2">{formatEnumLabel(vol.volume_status)}</td>
                    <td className="px-3 py-2">{formatEnumLabel(vol.protection_status)}</td>
                    <td className="px-3 py-2">{formatEnumLabel(vol.encryption_method)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {vol.encryption_percentage != null
                        ? `${vol.encryption_percentage}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">
          No BitLocker volumes reported on this device.
        </p>
      )}
    </SnapshotCard>
  )
}

function OfficeMacrosSnapshotCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const allowed = data.macros_allowed
  const findings = Array.isArray(data.findings) ? data.findings : []
  const policyFound = data.policy_found === true || findings.length > 0
  const pill =
    allowed === true ? 'Allowed' : allowed === false ? 'Blocked' : 'Unknown'
  const pillVariant =
    allowed === true ? 'pending' : allowed === false ? 'ready' : 'inactive'

  return (
    <SnapshotCard
      title="Office macros"
      collectedAt={snapshot.collected_at}
      pill={pill}
      pillVariant={pillVariant}
    >
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Macros allowed to run</span>
        <YesNoBadge value={allowed} invert />
      </div>
      <SnapshotField label="Summary" value={data.summary} />
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Blocking policy found</span>
        <YesNoBadge value={policyFound} />
      </div>
      <SnapshotField label="Profiles checked" value={data.profiles_checked} />
      <SnapshotField label="Profiles with policy" value={data.profiles_with_policy} />

      {findings.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Policy findings</p>
          <div className="rounded-md border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Scope</th>
                  <th className="px-3 py-2 font-medium">App</th>
                  <th className="px-3 py-2 font-medium">Profile</th>
                  <th className="px-3 py-2 font-medium">VBAWarnings</th>
                  <th className="px-3 py-2 font-medium">Allowed</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((row, index) => (
                  <tr
                    key={`${row.sid || row.scope}-${row.app}-${row.office_version}-${index}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2">{row.scope || '—'}</td>
                    <td className="px-3 py-2">
                      {row.app || '—'}
                      {row.office_version ? ` ${row.office_version}` : ''}
                    </td>
                    <td className="px-3 py-2 max-w-[12rem] truncate" title={row.profile_path || ''}>
                      {row.profile_path
                        ? String(row.profile_path).split('\\').pop()
                        : row.scope === 'machine'
                          ? 'Machine'
                          : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {row.vba_warnings_label ||
                        (row.vba_warnings != null ? String(row.vba_warnings) : '—')}
                    </td>
                    <td className="px-3 py-2">
                      <YesNoBadge value={row.macros_allowed} invert />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">
          No Office macro policy keys found — treated as allowed (not locked down) for cyber posture.
        </p>
      )}
    </SnapshotCard>
  )
}

function DotNet6SnapshotCard({ snapshot }) {
  const dotnet6 = detectDotNet6FromPayload(snapshot?.payload)
  const collectedAt = snapshot?.collected_at ?? null
  const summary = dotNet6MatchSummary(dotnet6.matches)

  if (!dotnet6.hasData) {
    return (
      <SnapshotCard title=".NET 6 (EOL)" collectedAt={collectedAt}>
        <p className="text-sm text-muted-foreground">
          No installed-software inventory yet. Deploy the installed-software RMM script
          from Atera to detect end-of-life .NET 6 runtimes on this device.
        </p>
      </SnapshotCard>
    )
  }

  const pill = dotnet6.installed ? 'Installed' : 'Not found'
  const pillVariant = dotnet6.installed ? 'destructive' : 'ready'
  const collapsedSummary = dotnet6.installed
    ? summary || '.NET 6 runtime detected'
    : 'No .NET 6 runtime reported'

  return (
    <SnapshotCard
      title=".NET 6 (EOL)"
      collectedAt={collectedAt}
      pill={pill}
      pillVariant={pillVariant}
      collapsedSummary={collapsedSummary}
      className={dotnet6.installed ? 'border-destructive/40' : undefined}
    >
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">.NET 6 installed</span>
        <YesNoBadge value={dotnet6.installed} invert />
      </div>
      <SnapshotField label="Matched products" value={summary || '—'} />
      {dotnet6.installed ? (
        <p className="pt-2 text-xs text-muted-foreground">
          .NET 6 reached end of support in November 2024. Plan removal or upgrade to a
          supported runtime (8 or later).
        </p>
      ) : null}
      {dotnet6.matches.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Matching inventory</p>
          <div className="rounded-md border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium">Version</th>
                  <th className="px-3 py-2 font-medium">Publisher</th>
                </tr>
              </thead>
              <tbody>
                {dotnet6.matches.map((row, index) => (
                  <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{row.name || '—'}</td>
                    <td className="px-3 py-2">{row.version || '—'}</td>
                    <td className="px-3 py-2">{row.publisher || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </SnapshotCard>
  )
}

function YesNoBadge({ value, invert = false }) {
  const yes = value === true
  const good = invert ? !yes : yes
  if (value !== true && value !== false) {
    return <span className="font-medium">—</span>
  }
  return (
    <Badge variant={good ? 'success' : 'warning'}>
      {yes ? 'Yes' : 'No'}
    </Badge>
  )
}

function TpmSecureBootSnapshotCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}

  return (
    <SnapshotCard title="TPM & Secure Boot" collectedAt={snapshot.collected_at}>
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">TPM present</span>
        <YesNoBadge value={data.tpm_present} />
      </div>
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">TPM ready</span>
        <YesNoBadge value={data.tpm_ready} />
      </div>
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">TPM enabled</span>
        <YesNoBadge value={data.tpm_enabled} />
      </div>
      <SnapshotField label="TPM version" value={data.tpm_version} />
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">UEFI boot</span>
        <YesNoBadge value={data.uefi} />
      </div>
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Secure Boot enabled</span>
        <YesNoBadge value={data.secure_boot_enabled} />
      </div>
    </SnapshotCard>
  )
}

function LocalAdminsSnapshotCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const members = Array.isArray(data.members) ? data.members : []

  return (
    <SnapshotCard title="Local administrators" collectedAt={snapshot.collected_at}>
      <SnapshotField label="Member count" value={data.member_count ?? members.length} />

      {members.length > 0 ? (
        <div className="pt-3">
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => (
                  <tr
                    key={`${member.name}-${index}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2 font-medium">{member.name || '—'}</td>
                    <td className="px-3 py-2">{member.object_class || member.type || '—'}</td>
                    <td className="px-3 py-2">{member.principal_source || member.source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">No local administrator members reported.</p>
      )}
    </SnapshotCard>
  )
}

function SnapshotCardBySlug({ snapshot }) {
  if (!snapshot) return null
  // Firewall detail lives on the Network tab — never render it under Security.
  if (snapshot.script_slug === 'windows-firewall') {
    return null
  }
  if (snapshot.script_slug === 'bitlocker') {
    return <BitLockerSnapshotCard snapshot={snapshot} />
  }
  if (snapshot.script_slug === 'office-macros') {
    return <OfficeMacrosSnapshotCard snapshot={snapshot} />
  }
  if (snapshot.script_slug === 'tpm-secureboot') {
    return <TpmSecureBootSnapshotCard snapshot={snapshot} />
  }
  if (snapshot.script_slug === 'local-admins') {
    return <LocalAdminsSnapshotCard snapshot={snapshot} />
  }
  return <GenericSnapshotCard snapshot={snapshot} />
}

function GenericSnapshotCard({ snapshot }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const entries = Object.entries(data)
  const title = snapshotTitle(snapshot.script_slug, snapshot.script_title)

  return (
    <SnapshotCard title={title} collectedAt={snapshot.collected_at}>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No data in snapshot.</p>
      ) : (
        entries.map(([key, value]) => (
          <SnapshotField
            key={key}
            label={formatPayloadKey(key)}
            value={
              Array.isArray(value) || (value && typeof value === 'object')
                ? JSON.stringify(value)
                : value
            }
          />
        ))
      )}
    </SnapshotCard>
  )
}

function ThreatDownPlaceholder() {
  const [open, setOpen] = useState(false)
  return (
    <CollapsePanel
      id="security-threatdown-placeholder"
      title="ThreatDown"
      collapsedSummary="Not synced yet"
      open={open}
      onOpenChange={setOpen}
      pill="No data"
      pillVariant="inactive"
      className="border-dashed"
    >
      <p className="text-sm text-muted-foreground">
        Not synced yet. Add ThreatDown credentials to the server .env and run a sync from Admin.
      </p>
    </CollapsePanel>
  )
}

function ThreatDownSnapshotCard({ threatdown }) {
  if (!threatdown) return null
  const status = (threatdown.protection_status || '').toLowerCase()
  const statusVariant =
    status === 'active' || status === 'protected' ? 'success' : status ? 'warning' : 'secondary'

  return (
    <SnapshotCard
      title="ThreatDown"
      collectedAt={threatdown.synced_at}
      pill={threatdown.protection_status || 'Unknown'}
      pillVariant={
        status === 'active' || status === 'protected'
          ? 'ready'
          : status
            ? 'pending'
            : 'inactive'
      }
    >
      <SnapshotField label="Site" value={threatdown.account_name} />
      <SnapshotField label="Display name" value={threatdown.display_name} />
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Protection status</span>
        <Badge variant={statusVariant}>
          {threatdown.protection_status || 'Unknown'}
        </Badge>
      </div>
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Agent online</span>
        <YesNoBadge value={threatdown.online} />
      </div>
      <SnapshotField label="Engine version" value={threatdown.engine_version} />
      <SnapshotField label="Last seen (ThreatDown)" value={formatDate(threatdown.last_seen_at)} />
      {threatdown.id && (
        <div className="pt-2">
          <Link
            to={`/threatdown/endpoints/${threatdown.id}`}
            className="text-sm text-primary hover:underline"
          >
            View full ThreatDown detail
          </Link>
        </div>
      )}
    </SnapshotCard>
  )
}

const SECTION_PILL_VARIANT = {
  green: 'ready',
  amber: 'pending',
  red: 'destructive',
  grey: 'inactive',
}

function SecuritySection({
  id,
  title,
  description,
  sectionStatus,
  open,
  onOpenChange,
  children,
}) {
  const traffic = sectionStatus ? TRAFFIC[sectionStatus.status] : null
  const issues = (Array.isArray(sectionStatus?.issues) ? sectionStatus.issues : [])
    .map((issue) => normaliseIssue(issue, sectionStatus?.status))
    .filter(Boolean)
  const showIssues = issues.length > 0 && traffic && traffic.key !== 'green' && traffic.key !== 'grey'
  const collapsedSummary = showIssues
    ? issues.map((issue) => issue.text).join(' · ')
    : description

  return (
    <div id={id} className="scroll-mt-4">
      <CollapsePanel
        id={`${id}-panel`}
        title={title}
        collapsedSummary={collapsedSummary}
        open={open}
        onOpenChange={onOpenChange}
        pill={traffic && traffic.key !== 'grey' ? traffic.label : undefined}
        pillVariant={traffic ? SECTION_PILL_VARIANT[traffic.key] : 'inactive'}
        bodyClassName="space-y-4"
        className={
          traffic?.key === 'red'
            ? 'border-destructive/40'
            : traffic?.key === 'amber'
              ? 'border-amber-500/30'
              : undefined
        }
      >
        <p className="text-xs text-muted-foreground">{description}</p>
        {showIssues ? (
          <ul className="space-y-0.5 text-sm text-foreground">
            {issues.map((issue) => (
              <li key={`${issue.status}:${issue.text}`} className="flex gap-2">
                <span className="text-muted-foreground" aria-hidden>
                  -
                </span>
                <span>{issue.text}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {children}
      </CollapsePanel>
    </div>
  )
}

function SecurityEventsTable({ events, scriptSlug, title, empty, collapsible = false }) {
  const [open, setOpen] = useState(false)
  const filtered = events.filter((e) => e.script_slug === scriptSlug)
  if (filtered.length === 0) return null

  const columnsBySlug = {
    'failed-logins': [
      {
        id: 'label',
        label: 'Event',
        sortValue: (e) => e.event_label?.toLowerCase() ?? '',
        render: (e) => e.event_label || '—',
      },
      {
        id: 'occurred_at',
        label: 'When',
        sortValue: (e) => (e.occurred_at ? new Date(e.occurred_at).getTime() : null),
        render: (e) => formatDate(e.occurred_at),
      },
    ],
    'local-admin-changes': [
      {
        id: 'action',
        label: 'Action',
        sortValue: (e) => e.payload?.action?.toLowerCase() ?? '',
        render: (e) => e.payload?.action || '—',
      },
      {
        id: 'member',
        label: 'Account',
        sortValue: (e) => (e.payload?.member || e.event_label || '').toLowerCase(),
        render: (e) => e.payload?.member || e.event_label || '—',
      },
      {
        id: 'changed_by',
        label: 'Changed by',
        sortValue: (e) => e.payload?.changed_by?.toLowerCase() ?? '',
        render: (e) => e.payload?.changed_by || '—',
      },
      {
        id: 'occurred_at',
        label: 'When',
        sortValue: (e) => (e.occurred_at ? new Date(e.occurred_at).getTime() : null),
        render: (e) => formatDate(e.occurred_at),
      },
    ],
    'defender-detections': [
      {
        id: 'threat_name',
        label: 'Threat',
        sortValue: (e) => (e.payload?.threat_name || e.event_label || '').toLowerCase(),
        render: (e) => e.payload?.threat_name || e.event_label || '—',
      },
      {
        id: 'severity',
        label: 'Severity',
        sortValue: (e) => e.payload?.severity?.toLowerCase() ?? '',
        render: (e) => e.payload?.severity || '—',
      },
      {
        id: 'path',
        label: 'Path',
        sortValue: (e) => e.payload?.path?.toLowerCase() ?? '',
        render: (e) => e.payload?.path || '—',
      },
      {
        id: 'occurred_at',
        label: 'When',
        sortValue: (e) => (e.occurred_at ? new Date(e.occurred_at).getTime() : null),
        render: (e) => formatDate(e.occurred_at),
      },
    ],
  }

  const columns = columnsBySlug[scriptSlug] ?? [
    {
      id: 'label',
      label: 'Event',
      sortValue: (e) => e.event_label?.toLowerCase() ?? '',
      render: (e) => e.event_label || '—',
    },
    {
      id: 'occurred_at',
      label: 'When',
      sortValue: (e) => (e.occurred_at ? new Date(e.occurred_at).getTime() : null),
      render: (e) => formatDate(e.occurred_at),
    },
  ]

  const table = (
    <SortableTable
      empty={empty}
      data={filtered}
      defaultSort={{ id: 'occurred_at', dir: 'desc' }}
      columns={columns}
    />
  )

  if (collapsible) {
    return (
      <CollapsePanel
        id={`security-events-${scriptSlug}`}
        title={title}
        collapsedSummary={`${filtered.length} event${filtered.length === 1 ? '' : 's'}`}
        open={open}
        onOpenChange={setOpen}
      >
        {table}
      </CollapsePanel>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium">{title}</h4>
      {table}
    </div>
  )
}

function snapshotsForSection(snapshots, sectionId) {
  const slugs = SNAPSHOT_SECTION_SLUGS[sectionId] || []
  return slugs
    .map((slug) => snapshots.find((s) => s.script_slug === slug))
    .filter(Boolean)
}

function eventsForSection(events, sectionId) {
  const slugs = EVENT_SECTION_SLUGS[sectionId] || []
  return events.filter((e) => slugs.includes(e.script_slug))
}

export function AgentSecurity({ loading, data, threatdown, agent, agentId, onNavigateTab }) {
  const [openSections, setOpenSections] = useState({})

  function setSectionOpen(sectionId, next) {
    setOpenSections((prev) => ({ ...prev, [sectionId]: next }))
  }

  function scrollToSection(sectionId) {
    if (sectionId === 'network') {
      onNavigateTab?.('network')
      return
    }
    setSectionOpen(sectionId, true)
    requestAnimationFrame(() => {
      document
        .getElementById(`security-section-${sectionId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  if (loading) {
    return <Skeleton className="h-48 w-full" />
  }

  const events = data?.events ?? []
  const snapshots = data?.snapshots ?? []
  const threatdownStatus = threatdown ?? data?.threatdown ?? null
  const avProduct = agent?.av_product ?? null
  const avStatus = agent?.av_status ?? null
  const defenderDns = snapshots.find((s) => s.script_slug === 'defender-dns')
  const hasAnyData = snapshots.length > 0 || events.length > 0 || threatdownStatus

  if (!hasAnyData) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          No security data for this device yet. Deploy Bekat Dash ingest scripts from Atera
          to populate this tab.
        </CardContent>
      </Card>
    )
  }

  const threatSnapshots = snapshotsForSection(snapshots, 'threat-protection')
  const threatEvents = eventsForSection(events, 'threat-protection')
  const encryptionSnapshots = snapshotsForSection(snapshots, 'encryption-boot')
  const healthSnapshots = snapshotsForSection(snapshots, 'system-health')
  const accessSnapshots = snapshotsForSection(snapshots, 'access')
  const accessEvents = eventsForSection(events, 'access')
  const localAdminsSnap = accessSnapshots.find((snap) => snap.script_slug === 'local-admins')
  const firewallSnap = snapshots.find((s) => s.script_slug === 'windows-firewall')
  const hasDns = Boolean(defenderDns?.payload?.dns1 || defenderDns?.payload?.dns2)

  const toolkitSummary = toolkitSummaryFromRmmData(data)
  const installedSoftwareSnap = snapshots.find((s) => s.script_slug === 'installed-software')
  const posture = evaluateSecurityPosture({
    snapshots,
    events,
    threatdown: threatdownStatus,
    avProduct,
    avStatus,
    exclusions: agent?.posture_exclusions,
  })
  const sectionById = Object.fromEntries(posture.sections.map((s) => [s.id, s]))
  const sectionMeta = Object.fromEntries(SECURITY_SECTIONS.map((s) => [s.id, s]))

  const showThreat = Boolean(
    defenderDns || threatSnapshots.length > 0 || threatdownStatus || threatEvents.length > 0,
  )
  const showEncryption = encryptionSnapshots.length > 0
  const showHealth = Boolean(installedSoftwareSnap)
  const showAccess = Boolean(localAdminsSnap) || accessEvents.length > 0
  const showNetwork =
    Boolean(firewallSnap) || hasDns || (sectionById.network && sectionById.network.status !== 'grey')
  const showToolkitLink = Boolean(toolkitSummary)

  return (
    <div className="space-y-4">
      <SecurityTrafficLight posture={posture} onSectionClick={scrollToSection} />

      {showThreat && (
        <SecuritySection
          id="security-section-threat-protection"
          title={sectionMeta['threat-protection'].title}
          description={sectionMeta['threat-protection'].description}
          sectionStatus={sectionById['threat-protection']}
          open={Boolean(openSections['threat-protection'])}
          onOpenChange={(next) => setSectionOpen('threat-protection', next)}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <DefenderSnapshotCard
              snapshot={defenderDns}
              threatdown={threatdownStatus}
              avProduct={avProduct}
              avStatus={avStatus}
            />
            {threatSnapshots.map((snap) => (
              <SnapshotCardBySlug key={snap.id} snapshot={snap} />
            ))}
            {threatdownStatus ? (
              <ThreatDownSnapshotCard threatdown={threatdownStatus} />
            ) : (
              <ThreatDownPlaceholder />
            )}
            <SecurityEventsTable
              events={events}
              scriptSlug="defender-detections"
              title="Defender detections"
              empty="No Defender detections reported yet."
              collapsible
            />
          </div>
        </SecuritySection>
      )}

      {showNetwork && (
        <SecuritySection
          id="security-section-network"
          title={sectionMeta.network.title}
          description={sectionMeta.network.description}
          sectionStatus={sectionById.network}
          open={Boolean(openSections.network)}
          onOpenChange={(next) => setSectionOpen('network', next)}
        >
          <Card>
            <CardContent className="space-y-2 pt-6 text-sm text-muted-foreground">
              <p>
                Firewall and DNS contribute to this Security score. Adapter detail, firewall
                profiles, DNS servers, and DHCP/DNS server health are shown on the Network tab.
              </p>
              <button
                type="button"
                className="inline-flex text-sm font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => onNavigateTab?.('network')}
              >
                Open Network tab
              </button>
            </CardContent>
          </Card>
        </SecuritySection>
      )}

      {showEncryption && (
        <SecuritySection
          id="security-section-encryption-boot"
          title={sectionMeta['encryption-boot'].title}
          description={sectionMeta['encryption-boot'].description}
          sectionStatus={sectionById['encryption-boot']}
          open={Boolean(openSections['encryption-boot'])}
          onOpenChange={(next) => setSectionOpen('encryption-boot', next)}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {encryptionSnapshots.map((snap) => (
              <SnapshotCardBySlug key={snap.id} snapshot={snap} />
            ))}
          </div>
        </SecuritySection>
      )}

      {showHealth && (
        <SecuritySection
          id="security-section-system-health"
          title={sectionMeta['system-health'].title}
          description={sectionMeta['system-health'].description}
          sectionStatus={sectionById['system-health']}
          open={Boolean(openSections['system-health'])}
          onOpenChange={(next) => setSectionOpen('system-health', next)}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {healthSnapshots.map((snap) => (
              <SnapshotCardBySlug key={snap.id} snapshot={snap} />
            ))}
            <DotNet6SnapshotCard snapshot={installedSoftwareSnap ?? null} />
            {showToolkitLink ? (
              <div className="md:col-span-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                Toolkit health is shown on the{' '}
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => onNavigateTab?.('overview')}
                >
                  Overview
                </button>{' '}
                tab.
              </div>
            ) : null}
          </div>
        </SecuritySection>
      )}

      {showAccess && (
        <SecuritySection
          id="security-section-access"
          title={sectionMeta.access.title}
          description={sectionMeta.access.description}
          sectionStatus={sectionById.access}
          open={Boolean(openSections.access)}
          onOpenChange={(next) => setSectionOpen('access', next)}
        >
          <div className="grid gap-4 md:grid-cols-2">
            {localAdminsSnap ? (
              <SnapshotCardBySlug key={localAdminsSnap.id} snapshot={localAdminsSnap} />
            ) : null}
            <SecurityEventsTable
              events={events}
              scriptSlug="failed-logins"
              title="Failed logins (today)"
              empty="No failed login events yet."
              collapsible
            />
            <SecurityEventsTable
              events={events}
              scriptSlug="local-admin-changes"
              title="Local admin changes"
              empty="No local admin change events yet."
              collapsible
            />
          </div>
        </SecuritySection>
      )}
    </div>
  )
}
